import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigService } from '../../_services/config.service';
import { CmpConversation, CmpMessage, ChatUser, CmpConversationMember, ChatSettings } from '../_models/messaging.model';

@Injectable({
	providedIn: 'root',
})
export class MessagingService {
	constructor(
		private http: HttpClient,
		private configService: ConfigService,
	) {}

	private get baseUrl(): string {
		return this.configService.companiesUrl;
	}

	/** Header per opt-out dello spinner globale (chiamate in background, es. polling pannelli). */
	private opts(silent: boolean): { observe: 'response'; headers?: { [k: string]: string } } {
		return silent ? { observe: 'response', headers: { 'X-No-Spinner': 'true' } } : { observe: 'response' };
	}

	// --- Conversazioni ---

	getConversations(silent: boolean = false): Observable<CmpConversation[]> {
		// Scoping per-azienda: nel portale web le conversazioni sono filtrate sull'azienda attiva
		// (le chat DIRECT si vedono solo se l'altro utente e' membro di quell'azienda; le COMPANY per conv_cmp_id).
		const cmpId = this.configService.getCompany_id();
		const q = cmpId ? `?cmp_id=${cmpId}` : '';
		return this.http.get(`${this.baseUrl}messaging/conversations${q}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	findOrCreateConversation(otherUsrId: number, silent: boolean = false): Observable<CmpConversation> {
		// cmp_id = azienda attiva: tagga la conversazione (conv_cmp_id) cosi' resta visibile
		// nello scoping per-azienda anche se uno dei due non e' membro (es. ADMIN portale).
		const cmpId = this.configService.getCompany_id();
		const body: any = { other_usr_id: otherUsrId };
		if (cmpId) body.cmp_id = cmpId;
		return this.http.post(`${this.baseUrl}messaging/conversations/find-or-create`, body, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	/**
	 * Chat azienda <-> cliente per l'azienda attiva (modalita' COMPANY): l'utente corrente
	 * e' il cliente, la controparte e' l'insieme dei membri dell'azienda. Da usare solo se
	 * l'utente NON e' membro dell'azienda, altrimenti aprirebbe una chat con se stesso.
	 */
	findOrCreateCompanyConversation(silent: boolean = false): Observable<CmpConversation> {
		const cmpId = this.configService.getCompany_id();
		return this.http.post(`${this.baseUrl}messaging/conversations/find-or-create-by-company`, { cmp_id: cmpId }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	// --- Messaggi ---

	getMessages(convId: number, silent: boolean = false): Observable<CmpMessage[]> {
		return this.http.get(`${this.baseUrl}messaging/messages/${convId}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	sendMessage(convId: number, text: string, silent: boolean = false): Observable<CmpMessage> {
		const formData = new FormData();
		formData.append('text', text);
		return this.http.post(`${this.baseUrl}messaging/messages/send/${convId}`, formData, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	sendMedia(convId: number, text: string, files: File[], silent: boolean = false): Observable<CmpMessage> {
		const formData = new FormData();
		formData.append('text', text || '');
		(files || []).forEach((f) => formData.append('files', f));
		return this.http.post(`${this.baseUrl}messaging/messages/send-media/${convId}`, formData, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	editMessage(msgId: number, text: string, silent: boolean = false): Observable<number> {
		return this.http.put(`${this.baseUrl}messaging/messages/edit/${msgId}`, { text }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	deleteMessage(msgId: number, silent: boolean = false): Observable<any> {
		return this.http.delete(`${this.baseUrl}messaging/messages/${msgId}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	deleteConversation(convId: number, silent: boolean = false): Observable<any> {
		return this.http.delete(`${this.baseUrl}messaging/conversations/${convId}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	getAttachmentBlob(attId: number): Observable<Blob> {
		return this.http.get(`${this.baseUrl}messaging/attachments/${attId}`, { responseType: 'blob', headers: { 'X-No-Spinner': 'true' } });
	}

	markAsRead(convId: number, silent: boolean = false): Observable<number> {
		return this.http.put(`${this.baseUrl}messaging/messages/read/${convId}`, {}, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	// --- Gruppi ---

	searchGroupUsers(txtsearch: string = '', silent: boolean = false): Observable<ChatUser[]> {
		return this.http.get(`${this.baseUrl}messaging/group-users?txtsearch=${encodeURIComponent(txtsearch)}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	createGroup(title: string, memberIds: number[], silent: boolean = false): Observable<CmpConversation> {
		return this.http.post(`${this.baseUrl}messaging/groups`, { title, member_ids: memberIds }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	getGroupMembers(convId: number, silent: boolean = false): Observable<CmpConversationMember[]> {
		return this.http.get(`${this.baseUrl}messaging/groups/${convId}/members`, this.opts(silent)).pipe(map((r: any) => r.body || []));
	}

	addGroupMember(convId: number, usrId: number, silent: boolean = false): Observable<any> {
		return this.http.post(`${this.baseUrl}messaging/groups/${convId}/members`, { usr_id: usrId }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	removeGroupMember(convId: number, usrId: number, silent: boolean = false): Observable<any> {
		return this.http.delete(`${this.baseUrl}messaging/groups/${convId}/members/${usrId}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	leaveGroup(convId: number, silent: boolean = false): Observable<any> {
		return this.http.post(`${this.baseUrl}messaging/groups/${convId}/leave`, {}, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	renameGroup(convId: number, title: string, silent: boolean = false): Observable<any> {
		return this.http.put(`${this.baseUrl}messaging/groups/${convId}`, { title }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	getUnreadCount(): Observable<number> {
		// Stesso scoping per-azienda di getConversations: il badge non deve contare
		// conversazioni che la lista scoped non mostra.
		const cmpId = this.configService.getCompany_id();
		const q = cmpId ? `?cmp_id=${cmpId}` : '';
		return this.http.get(`${this.baseUrl}messaging/unread-count${q}`, { observe: 'response', headers: { 'X-No-Spinner': 'true' } }).pipe(map((r: any) => r.body));
	}

	// --- Impostazioni chat / mute ---

	getChatSettings(silent: boolean = false): Observable<ChatSettings> {
		return this.http.get(`${this.baseUrl}messaging/settings`, this.opts(silent)).pipe(map((r: any) => ({ email_notify: r.body?.cus_email_notify === true })));
	}

	saveChatSettings(settings: ChatSettings, silent: boolean = false): Observable<any> {
		return this.http.post(`${this.baseUrl}messaging/settings`, { email_notify: settings.email_notify }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	setConversationMuted(convId: number, muted: boolean, silent: boolean = false): Observable<any> {
		return this.http.post(`${this.baseUrl}messaging/conversations/${convId}/mute`, { muted }, this.opts(silent)).pipe(map((r: any) => r.body));
	}

	// --- Utenti ---

	searchUsers(txtsearch: string = '', silent: boolean = false): Observable<ChatUser[]> {
		// cmp_id = azienda attiva: serve al backend per il ramo ADMIN (l'admin portale
		// vede i membri dell'azienda attiva anche senza esserne dipendente).
		const cmpId = this.configService.getCompany_id();
		const q = cmpId ? `&cmp_id=${cmpId}` : '';
		return this.http.get(`${this.baseUrl}messaging/users?txtsearch=${encodeURIComponent(txtsearch)}${q}`, this.opts(silent)).pipe(map((r: any) => r.body));
	}
}
